import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { useToast } from '../../components/ui/use-toast'
import { authService } from '../../../app/services/authService'
import { RegisterParams } from '../../../app/services/authService/register'
import { RegisterFormData, registerSchema, Step } from './components/types'
import { userIdentity } from '../../../app/store'
import { useBanners } from '@/app/hooks/useBanners'
import { BannerOrigin, BannerType } from '@/app/entities/Banner'
import { useWhitelabelContext } from '@/app/contexts/whitelabelContext'
import { useNavigate } from 'react-router-dom'

export const useRegisterController = () => {
  const [currentStep, setCurrentStep] = useState<Step>('personal-information')
  const { toast } = useToast()
  const [, setUserIdentity] = useAtom(userIdentity)
  const { banners, isLoading } = useBanners(BannerType.LOGIN, BannerOrigin.WEB)
  const { appConfig } = useWhitelabelContext()
  const navigate = useNavigate()

  const methods = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (data: RegisterParams) => {
      return authService.register(data)
    },
  })

  const goToNextStep = (step: Step) => {
    setCurrentStep(step)
  }

  const handleSubmit = async (data: RegisterFormData) => {
    try {
      const registerData: RegisterParams = {
        ...data.personalInfo,
        birthDate: new Date(data.personalInfo.birthDate).toISOString(),
        phone: data.personalInfo.phone.replace(/\D/g, ''),
        cpf: data.personalInfo.cpf.replace(/\D/g, ''),
        rg: data.personalInfo.rg
          ? {
              number: data.personalInfo.rg,
              uf: '',
              org: '',
              date: '',
            }
          : undefined,
        address: {
          ...data.address,
          zipCode: data.address.zipCode.replace(/\D/g, ""),
          number:
            data.address.number && data.address.number.trim() !== ""
              ? data.address.number
              : "S/N",
        },
        password: data.passwordInfo.password,
        bankAccount: {
          ...data.bankInfo,
          account: data.bankInfo.account.replace(/\D/g, ''),
        },
        emailMarketing: data.bankInfo.emailMarketing || false,
      }

      const response = await mutateAsync(registerData)

      if (response.blacklisted) {
        toast({
          variant: 'destructive',
          title: 'Não foi possível completar o cadastro',
          description: 'Entre em contato com o suporte.',
        })
        navigate('/signIn')
        return
      }

      setUserIdentity({
        id: response._id,
        name: data.personalInfo.name,
        email: data.personalInfo.email,
      })

      if (appConfig?.unico) return goToNextStep('validate-identity')

      navigate('/signIn')

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar conta',
        description: error.message,
      })
    }
  }

  return {
    methods,
    currentStep,
    goToNextStep,
    handleSubmit,
    isPending,
    banners,
    isLoading,
  }
}
